/**
 * Entity Router — public hook surface for all entity reads.
 *
 * Consumers never import a backing store directly. They call:
 *
 *   useEntity(id)                    → single entity, falls through space → global → byId cache.
 *   useEntitiesBySpace(spaceId)      → all entities cached for a space.
 *   useEntitiesByType(type)          → entities of a specific type, routed by registry.
 *   useGlobalEntities()              → every global entity.
 *   useDisplayableEntities(spaceId)  → space entities filtered through entityVisibility.
 *   useEntityById(id)                → re-exported from entityByIdCache (cross-space single-id).
 *
 * Non-hook accessors live on `entityRegistry` (registry.getById, getByType,
 * upsert, deleteEntity) — used by system commands and the live-update bus.
 *
 * Routing rules live in `entityRegistry.ts`. This file contains only React-
 * facing selectors built on top of the backing stores.
 */

import { useEffect, useMemo } from 'react';
import { useSpaceEntityStore, useSpaceEntities } from './spaceEntityStore';
import {
  useGlobalEntityStore,
  useGlobalEntitiesByType,
  useGlobalEntities as useAllGlobalEntities,
} from './globalEntityStore';
import { useEntityByIdCache } from './entityByIdCache';
import { entityRegistry, resolveScope } from './entityRegistry';
import { buildDisplayableEntities } from '../utils/entityVisibility';
import type { BaseEntityType } from '../types/entities';

/** Re-exports — keep all entity-read primitives behind a single import. */
export { useSpaceEntities, useSpaceEntity } from './spaceEntityStore';
export {
  useGlobalEntities as useAllGlobalEntities,
  useGlobalEntitiesByType,
  useConnectedIntegrations,
} from './globalEntityStore';
export { useEntityById } from './entityByIdCache';
export { entityRegistry, resolveScope } from './entityRegistry';

const EMPTY_ENTITIES: BaseEntityType[] = [];

/**
 * Reactive aggregate of every entity across every backing store.
 *
 * Replaces the legacy `useAllEntities()`. Most callers
 * actually want a narrower scope (`useEntitiesBySpace`, `useEntitiesByType`,
 * `useGlobalEntities`); this hook is for legacy filter-everything code that
 * we couldn't migrate to a tighter selector. Returns referentially stable
 * empty arrays when nothing is cached.
 *
 * Implementation note: the zustand selector must return a stable identity so
 * React's `useSyncExternalStore` snapshot cache holds across renders — see
 * the matching pattern in `EntitySidebar/hooks/useEntityData.ts`. We
 * subscribe to the space store's `bumpVersion` (a primitive that ticks on
 * every cache mutation) and rebuild the deduped array inside `useMemo`.
 */
export function useAllEntities(): BaseEntityType[] {
  const spaceBump = useSpaceEntityStore((s) => s.bumpVersion);
  const globalEntities = useAllGlobalEntities();

  const spaceEntities = useMemo(() => {
    void spaceBump;
    const caches = useSpaceEntityStore.getState().caches;
    let total = 0;
    for (const entry of caches.values()) total += entry.entities.length;
    if (total === 0) return EMPTY_ENTITIES;
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const entry of caches.values()) {
      for (const e of entry.entities) {
        if (!e?._id || seen.has(e._id)) continue;
        seen.add(e._id);
        out.push(e);
      }
    }
    return out.length === 0 ? EMPTY_ENTITIES : out;
  }, [spaceBump]);

  return useMemo(() => {
    if (spaceEntities.length === 0 && globalEntities.length === 0) return EMPTY_ENTITIES;
    if (globalEntities.length === 0) return spaceEntities;
    if (spaceEntities.length === 0) return globalEntities;
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const e of spaceEntities) {
      if (!e?._id || seen.has(e._id)) continue;
      seen.add(e._id);
      out.push(e);
    }
    for (const e of globalEntities) {
      if (!e?._id || seen.has(e._id)) continue;
      seen.add(e._id);
      out.push(e);
    }
    return out;
  }, [spaceEntities, globalEntities]);
}

/**
 * Whether the global entity store has finished its first load. Used by
 * gating UI ("don't render an empty state until we know what we have").
 *
 * Replaces the legacy `useEntitySystemReady()` and
 * `useEntitySystemReady()` patterns.
 */
export function useEntitySystemReady(): boolean {
  return useGlobalEntityStore((s) => s.lifecycle === 'ready');
}

/**
 * Single-entity read with cross-store fallback.
 *
 * Resolution order:
 *   1. spaceEntityStore (any cached space).
 *   2. globalEntityStore.
 *   3. entityByIdCache (which itself falls through to IDB then REST).
 *
 * The hook is reactive — any of the three stores mutating the entity will
 * trigger a re-render. When the entity is missing everywhere, we kick off
 * a fetch via the byId cache exactly once and return undefined until it
 * resolves.
 */
export function useEntity(id: string | null | undefined): BaseEntityType | undefined {
  // Subscribe to spaceEntityStore — first space cache that has it wins.
  const fromSpace = useSpaceEntityStore((s) => {
    if (!id) return undefined;
    for (const entry of s.caches.values()) {
      const e = entry.entityById.get(id);
      if (e) return e;
    }
    return undefined;
  });

  // Subscribe to globalEntityStore.
  const fromGlobal = useGlobalEntityStore((s) => (id ? s.byId.get(id) : undefined));

  // Subscribe to byId cache as a last resort.
  const fromById = useEntityByIdCache((s) => (id ? s.entries.get(id) : undefined));

  // Kick off a byId fetch if nothing is cached anywhere. Side effects must
  // run inside useEffect — calling fetch during render violates the rules of
  // hooks and triggers double-fetches under React StrictMode.
  const missing = !!id && !fromSpace && !fromGlobal && !fromById;
  useEffect(() => {
    if (!missing || !id) return;
    const cacheState = useEntityByIdCache.getState();
    if (cacheState.inflight.has(id)) return;
    cacheState.fetch(id).catch(() => {});
  }, [id, missing]);

  return fromSpace ?? fromGlobal ?? fromById;
}

/**
 * Type-filtered list, routed via the registry.
 *
 * - Global types (Space, Integration*, MCPConnection, Org) → globalEntityStore.
 * - Space-scoped types (everything else) → filter spaceEntityStore caches by
 *   entityType across every cached space.
 *
 * App-owned types (Mail.*, Tasks.*, Bank.*, Automation.*, Database.*, Contact)
 * default to the space scope at the registry level, but the canonical source
 * for those is the dedicated app store (mailAppStore, tasksAppStore, ...) —
 * prefer those store's own hooks when you need richer derived state
 * (folder counts, drafts, etc.).
 */
export function useEntitiesByType(entityType: string): BaseEntityType[] {
  const scope = resolveScope(entityType);

  const fromGlobal = useGlobalEntitiesByType(scope === 'global' ? entityType : '__none__');

  // For space-scoped types, gather across all cached spaces. Subscribe to
  // `bumpVersion` (stable primitive) and build the array in useMemo to keep
  // the snapshot cache stable; see useAllEntities for why.
  const spaceBump = useSpaceEntityStore((s) => (scope === 'space' ? s.bumpVersion : 0));
  const spaceMatches = useMemo(() => {
    if (scope !== 'space') return EMPTY_ENTITIES;
    void spaceBump;
    const caches = useSpaceEntityStore.getState().caches;
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const entry of caches.values()) {
      for (const e of entry.entities) {
        if (e.entityType !== entityType) continue;
        const id = e._id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(e);
      }
    }
    return out.length === 0 ? EMPTY_ENTITIES : out;
  }, [scope, entityType, spaceBump]);

  return scope === 'global' ? fromGlobal : spaceMatches;
}

/**
 * All space-scoped entities for the active space, filtered through the
 * canonical visibility predicate (`entityVisibility.buildDisplayableEntities`).
 *
 * Hook-shaped equivalent of the registry's "give me everything visible in this
 * space" query, with the canonical visibility predicate already applied.
 */
export function useDisplayableEntities(
  spaceId: string | null | undefined,
): BaseEntityType[] {
  const entities = useSpaceEntities(spaceId);
  return useMemo(() => buildDisplayableEntities(entities), [entities]);
}

/**
 * All displayable entities across every backing store (space caches + global
 * store). Used by legacy callers that pre-date space scoping — sidebars,
 * search, file popups — where filtering by active space happens elsewhere.
 *
 * Prefer `useDisplayableEntities(spaceId)` whenever the consumer is rendering
 * inside a known space; this hook is intentionally broader.
 */
export function useAllDisplayableEntities(): BaseEntityType[] {
  const entities = useAllEntities();
  return useMemo(() => buildDisplayableEntities(entities), [entities]);
}

/**
 * All trashed space-scoped entities for the active space.
 *
 * Note: cross-space trash views (RiverDriveApp, OpenDialog) cannot use this
 * hook — they fetch from REST directly via `entityService.queryEntities({
 * trashed: true })` because trashed entities are filtered out of cached
 * space slices.
 */
export function useTrashedEntitiesForSpace(
  spaceId: string | null | undefined,
): BaseEntityType[] {
  // We pull the raw cache (including trashed) and filter ourselves. Note the
  // current spaceEntityStore filters trashed at fetch time; this is mostly a
  // forward-compatible no-op selector that matches the future schema where
  // trashed entities stay in the cache.
  const entities = useSpaceEntities(spaceId);
  return useMemo(
    () => entities.filter((e) => (e as any).trashed === true && (e as any).hidden !== true),
    [entities],
  );
}

/**
 * Convenience — read-only namespace exposing the registry's lookup primitives
 * for non-hook code (system commands, app stores). Mutations live on
 * `entityActions` (orchestrated) or `entityRegistry` (raw routing) — keeping
 * the router as a reads-only surface preserves a clear write path.
 */
export const entityRouter = {
  getById: entityRegistry.getById,
  getByType: entityRegistry.getByType,
  resolveScope: entityRegistry.resolveScope,
};
